import React from 'react';
import {
  AppBar,
  Avatar,
  Box,
  IconButton,
  Toolbar,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../hooks/useLanguage';
import { useAdminNavLinks } from '../../hooks/useAdminNavLinks';
import CompactAppLogo from '../Titles/CompactAppLogo';

export const MOBILE_TOP_BAR_HEIGHT = 65;

const MobileTopBar: React.FC = () => {
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  const { navigateWithLang } = useLanguage();
  const { carouselLinks, homePath, displayName, email, isActive, handleNavClick } = useAdminNavLinks();

  const getInitials = () => {
    if (displayName) return displayName.charAt(0).toUpperCase();
    if (email) return email.charAt(0).toUpperCase();
    return 'A';
  };

  const iconButtonSx = (link: { comingSoon?: boolean }, active: boolean) => ({
    border: `1px solid ${active ? theme.palette.primary.main : theme.palette.primary.light}`,
    color: theme.palette.primary.main,
    bgcolor: link.comingSoon
      ? (theme.palette as { blue?: { lightTransparent?: string } }).blue?.lightTransparent || 'rgba(128, 173, 230, 0.17)'
      : active
        ? (theme.palette as { blue?: { lightTransparent?: string } }).blue?.lightTransparent || 'rgba(128, 173, 230, 0.17)'
        : 'background.paper',
    flexShrink: 0,
    width: 33,
    height: 33,
    opacity: link.comingSoon ? 0.85 : 1,
    '&:hover': {
      bgcolor:
        (theme.palette as { blue?: { lightTransparent?: string } }).blue?.lightTransparent || 'rgba(128, 173, 230, 0.12)',
      borderColor: theme.palette.primary.main,
    },
  });

  return (
    <AppBar
      position="fixed"
      color="default"
      elevation={1}
      sx={{
        height: MOBILE_TOP_BAR_HEIGHT,
        width: '100%',
        left: 0,
        right: 0,
        bgcolor: 'white',
        padding: '0 10px',
        color: 'text.primary',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: `1px solid ${theme.palette.primary.light}`,
        zIndex: (muiTheme) => muiTheme.zIndex.appBar,
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          minHeight: MOBILE_TOP_BAR_HEIGHT,
          height: MOBILE_TOP_BAR_HEIGHT,
          width: '100%',
          px: 1,
          gap: 0.75,
          color: 'inherit',
        }}
      >
        <CompactAppLogo compact={isNarrow} onClick={() => navigateWithLang(homePath)} />

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            overflowX: 'auto',
            overflowY: 'hidden',
            py: 0.25,
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 0.5,
              width: 'max-content',
              mx: 'auto',
            }}
          >
            {carouselLinks.map((link) => {
              const NavIcon = link.icon;
              const active = isActive(link.path);

              return (
                <Tooltip key={link.path} title={link.label} placement="bottom">
                  <span>
                    <IconButton
                      size="small"
                      aria-label={link.label}
                      aria-current={active ? 'page' : undefined}
                      disabled={link.comingSoon}
                      onClick={() => handleNavClick(link)}
                      sx={iconButtonSx(link, active)}
                    >
                      <NavIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              );
            })}
          </Box>
        </Box>

        <Tooltip title={displayName || t('sidebar.adminRole')} placement="bottom">
          <IconButton
            aria-label={displayName || t('sidebar.adminRole')}
            onClick={() => navigateWithLang(homePath)}
            edge="end"
            sx={{ p: 0.25, flexShrink: 0 }}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: theme.palette.primary.main,
                fontSize: '0.95rem',
              }}
            >
              {getInitials()}
            </Avatar>
          </IconButton>
        </Tooltip>
      </Toolbar>
    </AppBar>
  );
};

export default MobileTopBar;
